import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium'
import { FC, useEffect } from 'react'
import {
  Create,
  FormDataConsumer,
  ReferenceInput,
  required,
  SelectInput,
  SimpleForm,
  TextInput,
  useTranslate,
  useGetList,
} from 'react-admin'
import { useFormContext } from 'react-hook-form'
import { ActionsToolbar } from '../../components'
import { CertificatesCreateContact } from './forms/Contact.form'
import { Alert, Typography } from '@mui/material'
import { TargetSystem, MunicipalityOut, CertificateType } from '../../types/apiRequests'
import { ZakenForm } from './forms/zaken/Zaken.form'
import PrefillContactFromMunicipality from './forms/zaken/PrefillContactFromMunicipality'
import { getCapabilities } from './targetSystems'
import { hostnameValidation } from '../../utils/validation'

const SetDefaultMunicipality: FC<{ municipalityId: string }> = ({ municipalityId }) => {
  const { setValue, getValues } = useFormContext()

  useEffect(() => {
    const timeout = setTimeout(() => {
      const currentValue = getValues('municipalityId') as string | undefined
      if (!currentValue) {
        setValue('municipalityId', municipalityId, {
          shouldValidate: false,
          shouldDirty: false,
        })
      }
    }, 100)

    return () => clearTimeout(timeout)
  }, [municipalityId, setValue, getValues])

  return null
}

// Target system select. A switch clears metaData and a type the new system doesn't
// allow, so e.g. a Zaken instance/host never ends up on a Bezwaren certificate.
const TargetSystemSelect: FC = () => {
  const { setValue, getValues } = useFormContext()

  const onChange = (): void => {
    const allowedTypes = getCapabilities(getValues('targetSystem') as TargetSystem)?.allowedTypes ?? []
    setValue('metaData', {})
    if (allowedTypes.length === 1) {
      setValue('type', allowedTypes[0])
    } else if (!allowedTypes.includes(getValues('type') as CertificateType)) {
      setValue('type', null)
    }
  }

  return (
    <SelectInput
      source="targetSystem"
      label="resources.certificates.fields.targetSystem"
      validate={[required()]}
      choices={Object.values(TargetSystem).map((value) => ({
        id: value,
        name: `resources.certificates.targetSystems.${value.toLowerCase()}`,
      }))}
      onChange={onChange}
      fullWidth
    />
  )
}

// Type select. A switch clears the koppeling, as its choices differ per type: a stufconfig
// koppeling must not stay on a PKI certificate. The interface select then drops an interface
// of another module itself.
const TypeSelect: FC<{ allowedTypes: CertificateType[] }> = ({ allowedTypes }) => {
  const { setValue } = useFormContext()

  const onChange = (): void => {
    // undefined, so the PKI form's defaultValue applies again.
    setValue('metaData.samlKoppeling', undefined)
  }

  return (
    <SelectInput
      source="type"
      label="resources.certificates.fields.type"
      validate={[required()]}
      choices={allowedTypes.map((value) => ({
        id: value,
        name: `resources.certificates.types.${value.toLowerCase()}`,
      }))}
      onChange={onChange}
      fullWidth
    />
  )
}

const CertificatesCreate: FC = () => {
  const translate = useTranslate()

  const { data: municipalities } = useGetList<MunicipalityOut>('municipality')
  const hasAtLeastOneMunicipality = !!municipalities && municipalities.length > 0

  return (
    <Create
      actions={
        <ActionsToolbar
          startIcon={<WorkspacePremiumIcon />}
          i18n="resources.certificates.create"
        />
      }
      redirect="show"
    >
      <SimpleForm defaultValues={{ targetSystem: TargetSystem.ZAKEN }}>
        {hasAtLeastOneMunicipality ? (
          <>
            <SetDefaultMunicipality municipalityId={municipalities[0].id} />

            <TargetSystemSelect />

            <ReferenceInput source="municipalityId" reference="municipality">
              <SelectInput
                optionText="organisatienNaam"
                label="resources.certificates.fields.municipality"
                optionValue="id"
                validate={[required()]}
                fullWidth
              />
            </ReferenceInput>

            <Typography variant="h6">
              {translate('resources.certificates.fields.contactDetails')}
            </Typography>
            <PrefillContactFromMunicipality />
            <CertificatesCreateContact />

            <Typography variant="h6">
              {translate('resources.certificates.fields.certificateDetails')}
            </Typography>

            <FormDataConsumer>
              {({ formData }) => {
                const capabilities = getCapabilities(formData?.targetSystem as TargetSystem)
                return (
                  <>
                    {/* Only the types the target system supports. */}
                    <TypeSelect allowedTypes={capabilities?.allowedTypes ?? []} />
                    {/* Systems without own hostname logic (e.g. Bezwaren): user enters it. */}
                    {capabilities?.commonName === 'manual' && (
                      <TextInput
                        source="metaData.commonName"
                        label="resources.certificates.fields.host"
                        validate={[required(), ...hostnameValidation]}
                        fullWidth
                      />
                    )}
                    {formData?.targetSystem === TargetSystem.ZAKEN && <ZakenForm />}
                  </>
                )
              }}
            </FormDataConsumer>
          </>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {translate('resources.certificates.alerts.noMunicipalityConfigured')}
          </Alert>
        )}
      </SimpleForm>
    </Create>
  )
}

export default CertificatesCreate